import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DndContext, closestCenter, PointerSensor, TouchSensor, KeyboardSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable, arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { EyeOff, Eye, ChevronDown } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '../ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;
const RENAMED = { bank: 'programs' };
const tabCls = (active) => `flex items-center gap-2 font-heading text-xs tracking-widest uppercase px-4 py-3 border-b-2 whitespace-nowrap transition-colors ${active ? 'border-brand text-brand' : 'border-transparent text-muted-foreground hover:text-foreground'}`;

function SortableTab({ tab, active, onClick, onHide }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tab.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      style={{ transform: CSS.Translate.toString(transform), transition, zIndex: isDragging ? 10 : undefined }}
      className={`group relative flex items-center touch-manipulation select-none ${isDragging ? 'opacity-80 bg-muted cursor-grabbing' : ''}`}
    >
      <button type="button" onClick={onClick} data-testid={`tab-${tab.id}`} title="Click to open · drag to reorder" className={`${tabCls(active)} cursor-pointer`}>
        <tab.icon size={14} />
        {tab.label}
      </button>
      <button type="button" onClick={(e) => { e.stopPropagation(); onHide(); }} data-testid={`tab-hide-${tab.id}`}
        title="Hide this tab (moves to More)"
        className="absolute -top-0.5 right-0 p-1 rounded text-muted-foreground hover:text-brand opacity-0 group-hover:opacity-100 focus:opacity-100 [@media(hover:none)]:opacity-60 transition-opacity">
        <EyeOff size={11} />
      </button>
    </div>
  );
}

function MoreMenu({ hiddenTabs, activeTab, onSelect, onShow }) {
  const activeHidden = hiddenTabs.find(t => t.id === activeTab);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" data-testid="tab-more" className={tabCls(!!activeHidden)}>
          {activeHidden ? <><activeHidden.icon size={14} />{activeHidden.label}</> : 'More'}
          <span className="text-[10px] font-mono bg-muted rounded px-1">{hiddenTabs.length}</span>
          <ChevronDown size={12} />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64" data-testid="tab-more-menu">
        <DropdownMenuLabel className="text-[10px] tracking-widest uppercase text-muted-foreground">Hidden tabs</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {hiddenTabs.map(t => (
          <DropdownMenuItem key={t.id} onSelect={() => onSelect(t.id)} data-testid={`tab-more-item-${t.id}`} className="flex items-center gap-2 cursor-pointer">
            <t.icon size={14} />
            <span className="flex-1 font-heading text-xs tracking-widest uppercase">{t.label}</span>
            <button type="button" data-testid={`tab-show-${t.id}`} title="Show in menu again"
              onClick={(e) => { e.stopPropagation(); e.preventDefault(); onShow(t.id); }}
              className="p-1 rounded text-muted-foreground hover:text-brand hover:bg-muted">
              <Eye size={13} />
            </button>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const SortableTabBar = ({ tabs, activeTab, onSelect }) => {
  const [order, setOrder] = useState(tabs.map(t => t.id));
  const [hidden, setHidden] = useState([]);
  const [ready, setReady] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const known = tabs.map(t => t.id);
  const clean = (ids) => (ids || []).map(id => RENAMED[id] || id).filter(id => known.includes(id));

  useEffect(() => {
    axios.get(`${API}/api/admin/tab-order`).then(r => {
      setOrder([...new Set([...clean(r.data.order), ...known])]);
      setHidden(clean(r.data.hidden));
    }).catch(() => {}).finally(() => setReady(true));
    // eslint-disable-next-line
  }, []);

  const save = (patch, msg) => axios.put(`${API}/api/admin/tab-order`, patch)
    .then(() => toast.success(msg, { position: 'bottom-right' })).catch(() => toast.error('Could not save menu', { position: 'bottom-right' }));

  const visible = order.filter(id => !hidden.includes(id));

  const onDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const next = arrayMove(order, order.indexOf(active.id), order.indexOf(over.id));
    setOrder(next);
    save({ order: next }, 'Menu order saved for all admins');
  };
  const hide = (id) => {
    if (visible.length <= 1) return toast.error('Keep at least one tab visible', { position: 'bottom-right' });
    const next = [...hidden, id];
    setHidden(next);
    save({ hidden: next }, 'Tab hidden for all admins. Find it under More.');
  };
  const show = (id) => {
    const next = hidden.filter(h => h !== id);
    setHidden(next);
    save({ hidden: next }, 'Tab is back in the menu');
  };

  const byId = Object.fromEntries(tabs.map(t => [t.id, t]));
  const hiddenTabs = order.filter(id => hidden.includes(id)).map(id => byId[id]);
  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
      <SortableContext items={visible} strategy={rectSortingStrategy}>
        <div className={`max-w-6xl mx-auto px-4 sm:px-6 flex items-center overflow-x-auto md:overflow-visible md:flex-wrap gap-x-1 pb-0 transition-opacity ${ready ? 'opacity-100' : 'opacity-0'}`} data-testid="admin-tab-bar">
          {visible.map(id => (
            <SortableTab key={id} tab={byId[id]} active={activeTab === id} onClick={() => onSelect(id)} onHide={() => hide(id)} />
          ))}
          {hiddenTabs.length > 0 && <MoreMenu hiddenTabs={hiddenTabs} activeTab={activeTab} onSelect={onSelect} onShow={show} />}
        </div>
      </SortableContext>
    </DndContext>
  );
};
